import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { OnGroupDataMessageArgs, WebPubSubClient } from '@azure/web-pubsub-client';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { AlertHubEvent, eAlertHubEventType, eAlertStatus, CANCEL_ON_NAVIGATION } from 'reg-hub-common';
import { Constants } from '../../../../constants';
import { EnvironmentUrlService } from '../../environment-url/environment-url.service';

const START_RETRY_DELAY_MS = 10000;

@Injectable({
  providedIn: 'root'
})
export class AlertsHubService implements OnDestroy {
  private client: WebPubSubClient | undefined;

  // Count of the corporation's alerts still in New status; drives the Alerts sidebar badge
  private newAlertCountSource = new BehaviorSubject<number>(0);
  newAlertCount$ = this.newAlertCountSource.asObservable();

  private alertEventsSubject = new Subject<AlertHubEvent>();
  alertEvents$ = this.alertEventsSubject.asObservable();

  constructor(private http: HttpClient,
    private environmentUrl: EnvironmentUrlService) { }

  connect(): void {
    if (this.client) {
      return;
    }

    this.client = new WebPubSubClient({
      // Negotiate on every (re)connect since access URLs expire. The API joins the connection to the
      // user's corporation group when issuing the URL, so there's no group to join here.
      getClientAccessUrl: async () => {
        const response = await firstValueFrom(
          this.http.get<{ url: string }>(`${this.environmentUrl.urlAddress}${Constants.alertsHubNegotiateUrl}`, { context: new HttpContext().set(CANCEL_ON_NAVIGATION, false) }));
        return response.url;
      }
    }, {
      // Each reconnect attempt negotiates with the API, so back off instead of retrying every second
      reconnectRetryOptions: { mode: "Exponential" }
    });

    this.client.on("group-message", (messageEvent) => {
      this.handleGroupMessageReceived(messageEvent);
    });

    this.client.on("disconnected", () => {
      console.warn("Disconnected from Alerts hub");
    });

    this.start();
  }

  // Once started, the client reconnects by itself, but a failed start (e.g. the negotiate fails) is never retried
  private start() {
    this.client?.start()
      .catch(err => {
        console.error('Error starting Alerts hub client', err);
        setTimeout(() => this.start(), START_RETRY_DELAY_MS);
      });
  }

  setInitialNewAlertCount(count: number) {
    this.newAlertCountSource.next(count);
  }

  private handleGroupMessageReceived(messageEvent: OnGroupDataMessageArgs) {
    if (messageEvent.message.dataType === "text") {
      // The API serializes with camelCase property names and string enums
      const alertEvent = JSON.parse(messageEvent.message.data as string) as AlertHubEvent;

      this.updateNewAlertCount(alertEvent);
      this.alertEventsSubject.next(alertEvent);
    }
  }

  private updateNewAlertCount(alertEvent: AlertHubEvent) {
    const isNew = alertEvent.eventType !== eAlertHubEventType.Deleted
      && alertEvent.alert.alertStatusID === eAlertStatus.New;

    const wasNew = alertEvent.eventType === eAlertHubEventType.Updated
      ? alertEvent.previousAlertStatusID === eAlertStatus.New
      : alertEvent.eventType === eAlertHubEventType.Deleted && alertEvent.alert.alertStatusID === eAlertStatus.New;

    const delta = (isNew ? 1 : 0) - (wasNew ? 1 : 0);

    if (delta !== 0) {
      this.newAlertCountSource.next(Math.max(0, this.newAlertCountSource.value + delta));
    }
  }

  ngOnDestroy() {
    this.client?.stop();
    this.client = undefined;
  }
}
